// hooks/useFormViewerWizard/step1Submit.ts — Wizard step 1: insert order Pending.
// Sama seperti submit form sederhana (validasi + guard + RPC submit_public_order),
// tapi TANPA navigasi/CAPI/WA — order dilengkapi di step 2-3 via update_wizard_order.
// Guard ongkir dilewati (form di-override 'none'): kurir dipilih di step 2.
import { supabase } from '../../../../supabase/client';
import { normalizePhoneForDb } from '../../../../utils/formatPhone';
import { trackFormSubmit } from '../../../../utils/trackingUtils';
import { storePhoneLast4 } from '../../../../hooks/useThankYouPage/fetchOrder';
import { buildOrderPayload } from '../useFormViewer/submitPayload';
import { runSubmitGuards } from '../useFormViewer/submitGuards';
import { mapValidationError } from '../useFormViewer/actionsSubmit';
import { fireSubmitTracking } from '../useFormViewer/submitTracking';
import { patchWizardOrder } from './patchWizardOrder';
import { runStep1Aftermath } from './step1Aftermath';
import { stashStep1Capi } from './step1CapiStash';

export type Step1Result = { ok: true; orderId: string; assignedCsId: string | null } | { ok: false; message: string };

function newOrderId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = Math.random() * 16 | 0;
        return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
}

export async function submitWizardStep1(args: { hook: any; form: any; minMid: number }): Promise<Step1Result> {
    const { hook, form, minMid } = args;
    const errs = hook.validateCustomerFields();
    if (Object.keys(errs).length > 0) {
        hook.setFieldErrors(errs);
        return { ok: false, message: 'Lengkapi data yang wajib diisi dulu ya.' };
    }
    hook.setFieldErrors({});

    fireSubmitTracking({
        form, currentCombination: hook.currentCombination, quantity: hook.quantity,
        identifier: form?.slug || '', formPagePixels: hook.formPagePixels, formPagePixelConfigs: hook.formPagePixelConfigs,
    });

    const orderId = newOrderId();
    const uniqueCode = Math.min(100, Math.max(1, (Date.now() % 100000) % 100 + 1));
    // Override mode 'none' KHUSUS guard: step 1 = data simpel, tarif/kurir
    // menyusul di step 2. Server tetap baca mode 'wizard' dari tabel forms.
    const guardErr = await runSubmitGuards({
        form: { ...form, addressInputMode: 'none' },
        addressData: hook.addressData, selectedPaymentKey: undefined, selectedDuitkuMethod: null,
        selectedEverproRate: null, selectedBiteshipRate: null, shippingCost: 0, isCODVal: false, minMid,
        brandEverpro: hook.brandEverpro, brandMengantar: hook.brandMengantar, brandLincah: (hook as any).brandLincah,
        rateHold: false, lowPerfNoCod: false, selectedShippingKey: hook.selectedShippingKey,
    });
    if (guardErr) return { ok: false, message: guardErr };

    const built = await buildOrderPayload({
        form, customerData: hook.customerData, addressData: hook.addressData,
        selectedOptions: hook.selectedOptions, selectedShippingKey: hook.selectedShippingKey,
        selectedPaymentKey: undefined, selectedDuitkuMethod: null,
        selectedEverproRate: null, selectedBiteshipRate: null,
        currentCombination: hook.currentCombination, linkedProductName: hook.linkedProductName || '',
        quantity: hook.quantity, shippingCost: 0, codFee: 0, orderId, uniqueCode,
        isCODVal: false, isSimpleEmbed: false, herbalClaimMode: undefined,
        discountAmount: hook.discountAmount || 0, voucherCode: hook.couponApplied?.code || '',
        sourceFormOverride: 'wizard_step1',
        bumpProductIds: hook.selectedBumpIds || [],
        bumpSum: hook.bumpSum || 0,
    });

    const { data, error } = await supabase
        .rpc('submit_public_order', { p_order: built.pOrder })
        .abortSignal(AbortSignal.timeout(20000));
    if (error) {
        if (error.message?.includes('PESANAN_DUPLIKAT')) {
            return { ok: false, message: 'Kamu sudah punya pesanan produk ini yang sedang diproses. Kalau mau dibantu, hubungi admin ya.' };
        }
        const friendly = mapValidationError(error.message);
        return { ok: false, message: friendly || 'Gagal membuat pesanan. Silakan coba lagi.' };
    }
    const orderData = Array.isArray(data) ? data[0] : data;
    if (!orderData?.id) return { ok: false, message: 'Gagal membuat pesanan. Silakan coba lagi.' };

    // SOP-ALAMAT §19: kunci alamat ASLI customer ke customer_raw_address sejak
    // detik pertama (RPC hanya mengisi bila kosong — tak pernah menimpa).
    // Gagal = sweep backend yang backfill dari shippingAddress (sama mentahnya).
    try {
        const rawAddr = String(built.pOrder?.shippingAddress || '').trim();
        if (rawAddr) await patchWizardOrder(orderData.id, { customer_raw_address: rawAddr });
    } catch { /* abaikan — backfill sweep */ }
    // Stash CAPI sejak step 1 (tanpa direct-send): order step-1 yang closing
    // tanpa step 3 tetap terdengar Meta via sweep.
    stashStep1Capi({ hook, form, orderId: orderData.id });

    await runStep1Aftermath({ hook, form, built, orderData });
    return { ok: true, orderId: orderData.id, assignedCsId: built.assignedCsId || null };
}
